import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const currentUser = authService.getCurrentUser();
    if (currentUser && authService.isAuthenticated()) {
      setUser(currentUser);
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const { user: loggedInUser } = await authService.login(email, password);
      
      // Set user state immediately
      setUser(loggedInUser);
      
      // Double-check that user was stored correctly
      const storedUser = authService.getCurrentUser();
      if (!storedUser) {
        throw new Error('Failed to store user session');
      }
      
      return { success: true };
    } catch (error) {
      console.error('AuthContext login error:', error);
      
      // Extract error message from various possible locations
      let errorMessage = 'Login failed. Please check your credentials.';
      
      if (error.response?.data) {
        // Handle nested response structure
        const responseData = error.response.data;
        errorMessage = responseData.message || 
                      responseData.error || 
                      responseData.data?.message ||
                      errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        error: errorMessage,
      };
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  const value = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
